import { Box, Typography, Button, Stack, Paper, Container } from '@mui/material'
import { Link } from 'react-router'
import routes from './routes'
import AppBar from '../components/AppBar'
import ScubaDivingIcon from '@mui/icons-material/ScubaDiving'
import GroupsIcon from '@mui/icons-material/Groups'
import VerifiedIcon from '@mui/icons-material/Verified'

const Welcome = () => {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafbfc' }}>
      <AppBar
        title='Slacktide'
        actions={
          <Stack direction='row' spacing={1}>
            <Button
              color='primary'
              size='small'
              component={Link}
              to={routes.signin}
              variant='outlined'
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Join
            </Button>
            <Button
              color='primary'
              size='small'
              component={Link}
              to={routes.signin}
              variant='contained'
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Login
            </Button>
          </Stack>
        }
      />

      {/* Hero Section */}
      <Box
        sx={{
          background:
            'linear-gradient(135deg, #0d47a1 0%, #1565c0 40%, #0097a7 100%)',
          color: 'white',
          py: { xs: 6, md: 10 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth='md'>
          <Box
            component='img'
            src='/logo192.png'
            alt='Slacktide'
            sx={{
              width: { xs: 120, md: 160 },
              height: 'auto',
              mb: 3,
              filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
              borderRadius: '50%'
            }}
          />

          <Typography
            variant='h3'
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            Dive Together
          </Typography>

          <Typography
            variant='h6'
            sx={{
              opacity: 0.9,
              maxWidth: 500,
              mx: 'auto',
              mb: 4,
              fontWeight: 400,
              lineHeight: 1.6
            }}
          >
            List your dives, find a buddy, or join a guided trip. The easiest
            way to connect with divers near you.
          </Typography>

          <Button
            variant='contained'
            size='large'
            component={Link}
            to={routes.signup}
            sx={{
              px: 5,
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              fontSize: '1.1rem',
              bgcolor: 'white',
              color: '#0d47a1',
              fontWeight: 600,
              '&:hover': { bgcolor: '#f5f5f5' }
            }}
          >
            Get Started — It's Free
          </Button>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth='md' sx={{ py: { xs: 6, md: 8 } }}>
        <Typography
          variant='h5'
          sx={{ textAlign: 'center', fontWeight: 600, mb: 5 }}
        >
          Everything you need to plan your next dive
        </Typography>

        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              transition: 'box-shadow 0.2s',
              '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }
            }}
          >
            <ScubaDivingIcon color='primary' sx={{ fontSize: 44, mt: 0.5 }} />
            <Box>
              <Typography variant='h6' sx={{ mb: 0.5, fontWeight: 600 }}>
                List Your Dives
              </Typography>
              <Typography color='text.secondary' sx={{ lineHeight: 1.7 }}>
                Planning a dive? Post it and let others know where and when
                you're heading out. Open spots for buddies or guided groups.
              </Typography>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              transition: 'box-shadow 0.2s',
              '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }
            }}
          >
            <GroupsIcon color='primary' sx={{ fontSize: 44, mt: 0.5 }} />
            <Box>
              <Typography variant='h6' sx={{ mb: 0.5, fontWeight: 600 }}>
                Find a Buddy
              </Typography>
              <Typography color='text.secondary' sx={{ lineHeight: 1.7 }}>
                Never dive alone. Browse upcoming dives and join one that fits
                your schedule and skill level. Connect with fellow divers in
                your area.
              </Typography>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              transition: 'box-shadow 0.2s',
              '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }
            }}
          >
            <VerifiedIcon color='primary' sx={{ fontSize: 44, mt: 0.5 }} />
            <Box>
              <Typography variant='h6' sx={{ mb: 0.5, fontWeight: 600 }}>
                Dive Master Accredited Guides
              </Typography>
              <Typography color='text.secondary' sx={{ lineHeight: 1.7 }}>
                Guides on Slacktide are Dive Master accredited, so you can feel
                confident joining guided dives. Safety and experience you can
                trust.
              </Typography>
            </Box>
          </Paper>
        </Stack>
      </Container>

      {/* Bottom CTA */}
      <Box
        sx={{
          textAlign: 'center',
          py: 6,
          bgcolor: '#f0f4f8'
        }}
      >
        <Typography variant='h5' sx={{ fontWeight: 600, mb: 1 }}>
          Ready to dive in?
        </Typography>
        <Typography color='text.secondary' sx={{ mb: 3 }}>
          Join Slacktide today and find your next underwater adventure.
        </Typography>
        <Button
          variant='contained'
          size='large'
          component={Link}
          to={routes.signup}
          sx={{
            px: 5,
            py: 1.5,
            borderRadius: 3,
            textTransform: 'none',
            fontSize: '1.1rem'
          }}
        >
          Create Your Account
        </Button>
      </Box>
    </Box>
  )
}

export default Welcome
